import {LeftOutlined, RightOutlined, StarFilled, StarOutlined} from '@ant-design/icons';
import {Image} from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import Slider from 'react-slick';

import mobileBanner from '../../../assets/Images/mobileBanner.png';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import {RqGetActiveOffers} from '../../../reactQuery/apis';
import {selectAuth} from '../../../redux/selectors';

const Carousel = ({data}) => {
  const sliderRef = useRef(null);

  const [isWeb, setIsWeb] = useState(window.innerWidth > 768);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const [loadedImages, setLoadedImages] = useState(
    data?.images?.map(() => false),
  );

  const handleImageLoad = index => {
    setLoadedImages(prev => {
      const updated = [...prev];
      updated[index] = true;
      return updated;
    });
  };

  const auth = useSelector(selectAuth);
  const token = auth?.tokenData;

  const {data: offers} = RqGetActiveOffers();
  const activeOffer = offers?.[0] || null;

  useEffect(() => {
    const handleResize = () => setIsWeb(window.innerWidth > 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const settings = {
    dots: true,
    infinite: true,
    autoplay: false,
    speed: 500,
    fade: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplaySpeed: 5000,
    afterChange: index => setSelectedIndex(index),
    appendDots: dots => (
      <div
        style={{
          position: 'absolute',
          bottom: '6px',
          left: '50%',
          transform: 'translateX(-50%)',
        }}
      >
        <ul className={'m-0 p-0 text-2xl flex items-center justify-center lg:block'}>
          {dots}
        </ul>
      </div>
    ),
    customPaging: i => (
      <div
        className={`w-2 h-2 mx-1 rounded-full transition-all duration-300 cursor-pointer 
          ${i === selectedIndex ? 'opacity-100 bg-black' : 'opacity-40 bg-black'}`}
      />
    ),
  };

  return (
    <div className={'p-2 w-full 2xl:p-4'}>
      <div className={'w-full h-full overflow-hidden lg:relative rounded-2xl'}>

        {activeOffer ? (
          <div className={'w-full h-[25vh] lg:h-[60vh]'}>
            <div
              className={'shadow-lg h-full mb-8 w-full rounded-2xl'}
              style={{
                backgroundImage: `url(${
                  mobileBanner
                })`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
              }}
            >

              <div className={'flex flex-col justify-center h-full md:w-1/2 w-full md:ml-8 p-2'}>
                <p className={'md:text-8xl text-4xl font-extrabold w-full flex md:items-center md:justify-center italianno-regular md:pb-4 pb-2 text-primary-900 '}>
                  {activeOffer?.name || 'Exclusive Offer'}
                </p>
                <p className={'md:text-5xl text-2xl font-semibold w-full flex md:items-center md:justify-center gap-2 md:pb-2 pb-2 text-primary-900'}>UP TO  
                  <span className={'text-[#A30000]'}>{activeOffer?.value}%</span>OFF</p>
                <p className={'lg:text-xl text-sm text-[#8b0000] font-semibold md:w-full w-2/3 flex md:items-center md:justify-center font-serif-custom md:mt-4 mt-2'}>
                  {activeOffer?.description}
                </p>
                <div className={'w-full my-2 md:mt-8 mt-4 md:flex hidden items-center justify-center'}>
                  <div className={'flex-grow h-[1px] bg-gradient-to-r from-primary-800 via-primary-800 to-transparent'} />
                  <p className={'md:text-xl text-sm font-medium font-serif-custom '}>
                 Valid until: {dayjs(activeOffer?.validUntil).format('DD-MM-YYYY')}
                  </p>                  
                  <div className={'flex-grow h-[1px] bg-gradient-to-l from-primary-800 via-primary-800 to-transparent'} />
                </div>
                <p className={'md:text-xl my-2 mt-4 md:hidden flex text-sm font-medium font-serif-custom '}>
                  Valid until: {dayjs(activeOffer?.validUntil).format('DD_MM_YYYY')}
                </p>          
              </div>
            </div>
          </div>
        ) : (
          <>
            <Slider
              {...settings}
              ref={sliderRef}
              className={'lg:h-[60vh] w-full h-full'}
            >
              {data.images.map((item, index) => (
                <div
                  key={index}
                  className={'w-full h-[25vh] lg:h-[60vh] relative bg-gray-500'}
                >
                  <Image
                    preview={false}
                    src={item.image}
                    alt={`Slide ${index + 1}`}
                    width={'100%'}
                    height={'100%'}
                    className={`image-fill rounded-2xl transition-opacity duration-500 ${
                      index === selectedIndex && loadedImages[index]
                        ? 'animate-zoomEaseOut opacity-100'
                        : 'opacity-0'
                    }`}
                    onLoad={() => handleImageLoad(index)}
                  />
                </div>
              ))}
            </Slider>

            {isWeb && (
              <div>
                <div
                  role={'button'}
                  tabIndex={0}
                  onClick={() => sliderRef.current.slickPrev()}
                  onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && sliderRef.current.slickPrev()}
                  className={'absolute top-1/2 left-4 -translate-y-1/2 cursor-pointer'}
                >
                  <LeftOutlined className={'text-2xl p-2 rounded-full bg-white opacity-70'} />
                </div>

                <div
                  role={'button'}
                  tabIndex={0}
                  onClick={() => sliderRef.current.slickNext()}
                  onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && sliderRef.current.slickNext()}
                  className={'absolute top-1/2 right-2 -translate-y-1/2 cursor-pointer'}
                >
                  <RightOutlined className={'text-2xl p-2 rounded-full bg-white opacity-70'} />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

Carousel.propTypes = {
  data: PropTypes.shape({
    images: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        image: PropTypes.string.isRequired,
      }),
    ).isRequired,
  }).isRequired,
};

export default Carousel;
